import { Constants } from './common/Constants';
import { ResolveAPILoaded, ResolveQualtricsScriptLoadStatus } from './common/InternalTypes';
import { Logger } from './common/Logger';

/* global RequestMode */
export class QualtricsBridge {
	//Removes JavaScript from Creatives using DOMPurify
	private _qsiEnableJSSanitization = false;
	//Only allows variables to be read from JavaScript (can be defined using "." or "[]")
	private _qsiEnableSecureVariables = false;
	private _isApiLoaded = false;
	private _surveyTriggerButtonId = '';
	private readonly _logger: Logger;
	private _isCSPExceptionOccurred = false;
	private _qualtricsUrl: string;
	private _securityPolicyExceptionEventHandlerBound: (event: SecurityPolicyViolationEvent) => void;
	private _qualtricsScriptLoadedEventHandlerBound: (this: HTMLScriptElement, event: Event) => void;
	private _qualtricsScriptFailedEventHandlerBound: (this: HTMLScriptElement, event: Event) => void;
	private _qsiJsLoadedEventHandlerBound: (event: Event) => void;

	constructor() {
		this._logger = new Logger(Constants.FILE_INIT_CONTROLLER);
		this._isCSPExceptionOccurred = false;
	}

	get isApiLoaded(): boolean {
		return this._isApiLoaded;
	}

	get surveyTriggerButtonId(): string {
		return this._surveyTriggerButtonId;
	}

	get isQsiJSSanitizationEnabled(): boolean {
		return this._qsiEnableJSSanitization;
	}

	get isQsiSecureVariablesEnabled(): boolean {
		return this._qsiEnableSecureVariables;
	}

	get isCSPExceptionOccurred() {
		return this._isCSPExceptionOccurred;
	}

	get qualtricsUrl() {
		return this._qualtricsUrl;
	}

	public renderQualtricsButton(surveyTriggerButtonId: string) {
		if (document.body && surveyTriggerButtonId) {
			const existingButton = document.body.querySelector(`button[id="${surveyTriggerButtonId}"]`);
			if (!existingButton) {
				const standardTriggerButton = document.createElement('button');
				standardTriggerButton.id = surveyTriggerButtonId;
				standardTriggerButton.hidden = true;
				standardTriggerButton.type = 'button';
				document.body.appendChild(standardTriggerButton);
			}
			this._surveyTriggerButtonId = surveyTriggerButtonId;
		}
	}

	public async renderQualtricsScript(qualtricsUrl: string, enableQualtricsRestrictedMode: boolean): Promise<boolean> {
		return new Promise((resolve) => {
			if (enableQualtricsRestrictedMode) {
				this._qsiEnableJSSanitization = true;
				this._qsiEnableSecureVariables = true;
			}
			this._applySecuritySettings();
			if (document.head && qualtricsUrl) {
				this._qualtricsUrl = qualtricsUrl;
				const existingScript = this._getQualtricsScriptTag(qualtricsUrl);
				if (existingScript) {
					//As QSI API is being cleared in init of PxApi, remove old to rerun loading.
					existingScript.remove();
				}
				const qualtricsScript = document.createElement('script');
				qualtricsScript.type = 'text/javascript';
				qualtricsScript.src = qualtricsUrl;
				this._qualtricsScriptLoadedEventHandlerBound = this._qualtricsScriptEventHandler.bind(this, resolve, true);
				this._qualtricsScriptFailedEventHandlerBound = this._qualtricsScriptEventHandler.bind(this, resolve, false);
				qualtricsScript.addEventListener('load', this._qualtricsScriptLoadedEventHandlerBound);
				qualtricsScript.addEventListener('error', this._qualtricsScriptFailedEventHandlerBound);
				document.head.appendChild(qualtricsScript);
			} else {
				resolve(false);
			}
		});
	}

	public registerApiLoadedEvent(): Promise<boolean> {
		return new Promise((resolve) => {
			this._qsiJsLoadedEventHandlerBound = this._qsiJsLoadedEventHandler.bind(this, resolve);
			window.addEventListener('qsi_js_loaded', this._qsiJsLoadedEventHandlerBound, false);
		});
	}

	public async canLoadQualtricsScript(qualtricsUrl: string): Promise<boolean> {
		try {
			await fetch(qualtricsUrl, {
				method: 'HEAD',
				mode: 'no-cors' as RequestMode
			});
			return true;
		} catch (exception) {
			return false;
		}
	}

	public registerCSPExceptionEvent() {
		this._securityPolicyExceptionEventHandlerBound = this._securityPolicyExceptionEventHandler.bind(this);
		document.addEventListener('securitypolicyviolation', this._securityPolicyExceptionEventHandlerBound);
	}

	public deregisterQualtricsScriptLoadedEvent(qualtricsUrl: string) {
		const qualtricsScript = this._getQualtricsScriptTag(qualtricsUrl);
		if (qualtricsScript) {
			qualtricsScript.removeEventListener('load', this._qualtricsScriptLoadedEventHandlerBound);
			qualtricsScript.removeEventListener('error', this._qualtricsScriptFailedEventHandlerBound);
		}
	}

	public destroy() {
		//Ensure any event on any element does not hold any reference
		window.removeEventListener('qsi_js_loaded', this._qsiJsLoadedEventHandlerBound, false);
		document.removeEventListener('securitypolicyviolation', this._securityPolicyExceptionEventHandlerBound);
		this._removeSurveyTriggerButton();
		this._removeQualtricsScript();
		this._isCSPExceptionOccurred = false;
		this._securityPolicyExceptionEventHandlerBound = undefined!;
		this._qualtricsScriptLoadedEventHandlerBound = undefined!;
		this._qualtricsScriptFailedEventHandlerBound = undefined!;
		this._qsiJsLoadedEventHandlerBound = undefined!;
	}

	private _removeSurveyTriggerButton() {
		if (this._surveyTriggerButtonId) {
			const element = document.body.querySelector(`button[id="${this._surveyTriggerButtonId}"]`);
			if (element) {
				element.remove();
			}
			this._surveyTriggerButtonId = undefined!;
		}
	}

	private _removeQualtricsScript() {
		const qualtricsScript = this._getQualtricsScriptTag(this.qualtricsUrl);
		if (qualtricsScript) {
			qualtricsScript.remove();
		}
		this._qualtricsUrl = undefined!;
	}

	private _getQualtricsScriptTag(qualtricsUrl: string) {
		return document.head.querySelector(`script[src="${qualtricsUrl}"]`);
	}

	private _qsiJsLoadedEventHandler(resolve: ResolveAPILoaded) {
		const win: Window = window;
		this._isApiLoaded = false;
		if (win.QSI && win.QSI.API) {
			this._isApiLoaded = true;
		}
		resolve(this._isApiLoaded);
	}

	private _qualtricsScriptEventHandler(resolve: ResolveQualtricsScriptLoadStatus, loadSuccess: boolean) {
		resolve(loadSuccess);
	}

	private _securityPolicyExceptionEventHandler(event: SecurityPolicyViolationEvent) {
		this._isCSPExceptionOccurred = true;
		const cspViolationErrorMessage = `Content Security Policy(CSP) Error occurred, Please review your 'required' CSP Settings.
			Blocked URI / Missing Value: "${event.blockedURI}"
			Problematic Directive: "${event.violatedDirective}"
			Current CSP Policy: "${event.originalPolicy}"`;
		this._logger.error(cspViolationErrorMessage);
	}

	private _applySecuritySettings() {
		const win: Window = window;
		//Clear anything from any previous initialization
		win.QSI = undefined;
		if (this._qsiEnableJSSanitization && this._qsiEnableSecureVariables) {
			if (!win.QSI) {
				win.QSI = { global: {}, config: {} };
			}
			//"You should set these properties directly on the window.QSI object, before you load the JS snippet itself."
			win.QSI.global.enableJSSanitization = this._qsiEnableJSSanitization;
			win.QSI.config.enableSecureVariables = this._qsiEnableSecureVariables;
		}
	}
}
